/**
 * Helpers Web Push côté agent PWA.
 *
 * Encapsule l'enregistrement du Service Worker, la souscription au
 * navigateur (pushManager.subscribe), et l'envoi de la subscription au
 * backend Victor.
 */
import { api } from "./api";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function getExistingSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

export async function subscribePush(): Promise<PushSubscription | null> {
  if (!pushSupported()) {
    throw new Error("Notifications non supportées sur ce navigateur.");
  }
  const perm = await Notification.requestPermission();
  if (perm !== "granted") {
    throw new Error("Autorisation refusée pour les notifications.");
  }
  const cfg = await api.push.publicConfig();
  if (!cfg.vapidPublicKey) {
    throw new Error("Push non configuré côté serveur (VAPID manquant).");
  }
  const reg = await navigator.serviceWorker.ready;
  // Le typage DOM 2024 attend ArrayBufferView<ArrayBuffer> alors que
  // Uint8Array est typé sur ArrayBufferLike. On copie dans un buffer
  // dédié pour satisfaire les overloads.
  const keyBytes = urlBase64ToUint8Array(cfg.vapidPublicKey);
  const buf = new ArrayBuffer(keyBytes.byteLength);
  new Uint8Array(buf).set(keyBytes);
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: buf,
  });
  const json = sub.toJSON() as {
    endpoint?: string;
    keys?: { p256dh?: string; auth?: string };
  };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error("Souscription invalide.");
  }
  await api.push.subscribe({
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  });
  return sub;
}

export async function unsubscribePush(): Promise<void> {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  try {
    await api.push.unsubscribe(sub.endpoint);
  } catch {
    // on tente de désabonner côté navigateur même si l'API échoue
  }
  await sub.unsubscribe();
}
